import { Section } from '@/components/primitives/Section';
import { Card } from '@/components/ui/card';
import { Shield, CreditCard, Bell, Camera, Lock, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';

export function TrustSection() {
  return (
    <Section
      id="security"
      eyebrow="Enterprise-Grade Security"
      title="Bank-level security for something as simple as snow removal"
      subtitle="We take security, payments, and operator vetting as seriously as your bank does. Because trust isn't optional."
      background="frost"
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mt-16">
        {/* Left: Trust Features */}
        <div className="space-y-5">
          <motion.div 
            className="flex items-start gap-5 p-5 bg-white rounded-2xl border-2 border-brand-500/20 hover:border-brand-500 hover:shadow-lg transition-all group"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-500 to-brand-600 flex items-center justify-center flex-shrink-0 shadow-glow group-hover:scale-110 transition-transform">
              <CreditCard className="w-7 h-7 text-white" strokeWidth={2} />
            </div>
            <div>
              <h3 className="font-black text-lg mb-2 text-neutral-900 group-hover:text-brand-500 transition-colors">
                Stripe-Powered Payments
              </h3>
              <p className="text-sm text-neutral-700 leading-relaxed font-medium">
                We never see your card details. <strong>All payments processed through Stripe</strong> with bank-level encryption—the same system trusted by Amazon, Google, and Shopify.
              </p>
            </div>
          </motion.div>

          <motion.div 
            className="flex items-start gap-5 p-5 bg-white rounded-2xl border-2 border-accent-500/20 hover:border-accent-500 hover:shadow-lg transition-all group"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-accent-500 to-accent-600 flex items-center justify-center flex-shrink-0 shadow-glow-green group-hover:scale-110 transition-transform">
              <Shield className="w-7 h-7 text-white" strokeWidth={2} />
            </div>
            <div>
              <h3 className="font-black text-lg mb-2 text-neutral-900 group-hover:text-accent-500 transition-colors">
                Verified Operators Only
              </h3>
              <p className="text-sm text-neutral-700 leading-relaxed font-medium">
                Every operator verified through <strong>Stripe Identity</strong>—full KYC, background checks, insurance validation. No shortcuts, no exceptions.
              </p>
            </div>
          </motion.div>

          <motion.div 
            className="flex items-start gap-5 p-5 bg-white rounded-2xl border-2 border-brand-600/20 hover:border-brand-600 hover:shadow-lg transition-all group"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-600 to-brand-700 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
              <Bell className="w-7 h-7 text-white" strokeWidth={2} />
            </div>
            <div>
              <h3 className="font-black text-lg mb-2 text-neutral-900 group-hover:text-brand-600 transition-colors">
                Real-Time Updates
              </h3>
              <p className="text-sm text-neutral-700 leading-relaxed font-medium">
                Powered by <strong>Twilio</strong>. Get instant SMS and push notifications when matched, en route, arrived, and completed. Always know what's happening.
              </p>
            </div>
          </motion.div>

          <motion.div 
            className="flex items-start gap-5 p-5 bg-white rounded-2xl border-2 border-neutral-300 hover:border-neutral-900 hover:shadow-lg transition-all group"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-neutral-700 to-neutral-900 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
              <Camera className="w-7 h-7 text-white" strokeWidth={2} />
            </div>
            <div>
              <h3 className="font-black text-lg mb-2 text-neutral-900 group-hover:text-neutral-800 transition-colors">
                Photo Verification Required
              </h3>
              <p className="text-sm text-neutral-700 leading-relaxed font-medium">
                <strong>Before/after photos mandatory</strong> for every job. Timestamped and geo-tagged. Disputes resolved in under 24 hours by our support team.
              </p>
            </div>
          </motion.div>
        </div>

        {/* Right: Flow + Security Cards */}
        <div className="space-y-6">
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Card variant="solid" className="border-2 border-brand-500/30 shadow-glow">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-brand-500 to-brand-600 flex items-center justify-center">
                  <Lock className="w-6 h-6 text-white" strokeWidth={2.5} />
                </div>
                <h3 className="font-black text-2xl text-neutral-900">Payment Flow</h3>
              </div>
              
              <div className="space-y-4">
                {[
                  { step: 1, title: 'Reserve', desc: 'Card pre-authorized when you book', color: 'bg-brand-500' },
                  { step: 2, title: 'Complete', desc: 'Operator submits before/after photos', color: 'bg-brand-600' },
                  { step: 3, title: 'Approve', desc: 'You approve (or auto-approve after 2hrs)', color: 'bg-brand-700' },
                  { step: 4, title: 'Payout', desc: 'Operator gets paid, you get receipt', color: 'bg-accent-500' },
                ].map((item, index) => (
                  <motion.div 
                    key={item.step}
                    className="flex items-center gap-4 p-4 bg-neutral-50 rounded-xl hover:bg-brand-50 transition-colors group"
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                  >
                    <div className={`w-12 h-12 rounded-xl ${item.color} text-white flex items-center justify-center text-xl font-black shadow-lg group-hover:scale-110 transition-transform`}>
                      {item.step}
                    </div>
                    <div className="flex-1">
                      <p className="font-black text-base text-neutral-900">{item.title}</p>
                      <p className="text-xs text-neutral-600 font-medium leading-relaxed">{item.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Card variant="solid" className="border-2 border-accent-500/30 shadow-glow-green">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-accent-500 to-accent-600 flex items-center justify-center">
                  <Shield className="w-6 h-6 text-white" strokeWidth={2.5} />
                </div>
                <h3 className="font-black text-2xl text-neutral-900">Security Standards</h3>
              </div>
              
              <div className="space-y-3">
                {[
                  'TLS 1.3 encryption for all data in transit',
                  'AES-256 encrypted storage for sensitive data',
                  'PCI DSS compliant via Stripe',
                  'SOC 2 Type II certified infrastructure',
                  'Regular third-party security audits',
                  '24/7 fraud monitoring & detection',
                ].map((item, index) => (
                  <motion.div 
                    key={index}
                    className="flex items-start gap-3 p-3 bg-accent-50 rounded-xl"
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                  >
                    <CheckCircle className="w-5 h-5 text-accent-500 flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                    <p className="text-sm font-bold text-neutral-800">{item}</p>
                  </motion.div>
                ))}
              </div>
            </Card>
          </motion.div>

          <motion.div
            className="p-6 bg-gradient-to-br from-neutral-900 to-neutral-800 rounded-2xl text-white"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <p className="text-lg font-black mb-2">Your data is your data.</p>
            <p className="text-sm text-neutral-300 leading-relaxed">
              We'll never sell your information. We'll never spam you. We'll never share your data with third parties for marketing. 
              <span className="text-white font-bold"> Period.</span>
            </p>
          </motion.div>
        </div>
      </div>
    </Section>
  );
}

